import { Box, Link, Stack, Typography } from '@mui/material'
import { podiumStyles } from './podium.styles'
import { CSSProperties, FC, useCallback } from 'react';
import { LeaderboardPositionDto } from '@sweatpals/shared';
import { AvatarMock } from '../../../icons';
import { clsx } from '../../../utils';
import { Showable } from '../../utils';

interface PodiumProps {
  scoreName?: string;
  leaders: LeaderboardPositionDto[];
  prizes?: {
    gold: {
      prize?: string;
      goods?: string[];
    },
    silver: {
      prize?: string;
      goods?: string[];
    },
    bronze: {
      prize?: string;
      goods?: string[];
    }
  },
	homePageUrl?: string;
  images: {
    silverEffect: string;
    silverAvatarWrapper: string;
    silverPosition: string;
    silverPodium: string;
    silverTrophy: string;
    goldEffect: string;
    goldAvatarWrapper: string;
    goldPosition: string;
    goldPodium: string;
    goldTrophy: string;
    bronzeEffect: string;
    bronzeAvatarWrapper: string;
    bronzePosition: string;
    bronzePodium: string;
    bronzeTrophy: string;
    starsEffect: string;
    baseUrl: string;
  };
}

export const Podium: FC<PodiumProps> = ({ leaders, prizes, scoreName, images, homePageUrl }) => {
	const getCommunityPageUrl = useCallback((communityName?: string): string => {
		if (!communityName) {
			return '';
		}

		return `${homePageUrl ? `${homePageUrl}/` : ''}${communityName}`;
	}, [homePageUrl]);

  if (!leaders || leaders.length < 3) {
		return null;
	}

  const silverPlaceContent = (
    <>
      <Box sx={clsx(podiumStyles['silverWrapper'])}>
        <Box sx={clsx(podiumStyles['silverEffect'])}>
          <img src={images.silverEffect} alt="Silver effect" width={350} height={350} />
        </Box>
        <Box sx={clsx(podiumStyles['medalWrapper'])}>
          <img src={images.silverAvatarWrapper} alt="Silver avatar" width={114} height={116} />
          <Box sx={clsx(podiumStyles['avatarImage'])}>
            {leaders[1]?.community?.avatarId ? (
              <img src={`${images.baseUrl}/${leaders[1].community.avatarId}`} alt={leaders[1].community.fullName} style={podiumStyles['avatarImg'] as CSSProperties} />
            ) : (
              <AvatarMock style={{ borderRadius: '50%', width: '100%', height: '100%' }}/>
            )}
          </Box>
          <Box sx={clsx(podiumStyles['positionWrapper'])}>
            <img src={images.silverPosition} alt="Silver position" width={70} height={31} />
          </Box>
        </Box>
        <Box sx={clsx(podiumStyles['silverNameWrapper'])}>
          <Typography sx={clsx(podiumStyles['communityName'])}>{leaders[1]?.community?.fullName}</Typography>
          <Typography sx={clsx(podiumStyles['score'])}>{scoreName ? `${leaders[1]?.value} ${scoreName}` : ''}</Typography>
        </Box>
      </Box>
      <Box sx={clsx(podiumStyles['silverPodiumWrapper'])}>
        <img src={images.silverPodium} alt="Silver podium" width={257} height={180} />
        <Stack sx={{ position: 'absolute', top: '67px', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
					<Box sx={clsx(podiumStyles['prizeWrapper'])}>
						<img src={images.silverTrophy} alt="Silver trophy" width={prizes ? 30 : 60} height={prizes ? 31 : 62} />
						<Showable show={prizes?.silver?.prize}>
							<Typography sx={clsx(podiumStyles['prizeAmount'])}>
								$ {prizes?.silver?.prize}
							</Typography>
						</Showable>
					</Box>
					{prizes?.silver?.goods?.map((item, index) => (
						<Box sx={clsx(podiumStyles['goodsWrapper'])} key={`good-${index + 1}`}>
							<svg width="8" height="6" viewBox="0 0 8 6" fill="rgba(255, 255, 255, 0.7)" xmlns="http://www.w3.org/2000/svg">
								<path d="M7.65953 0.282573C7.80002 0.415388 7.88202 0.598559 7.88749 0.791812C7.89295 0.985066 7.82145 1.17258 7.6887 1.31313L3.55676 5.68813C3.48979 5.75891 3.40932 5.81555 3.32009 5.8547C3.23086 5.89385 3.1347 5.91473 3.03727 5.91609C2.93984 5.91744 2.84312 5.89926 2.75284 5.86261C2.66256 5.82597 2.58053 5.77159 2.51162 5.70271L0.324118 3.51521C0.195318 3.37699 0.125199 3.19416 0.128532 3.00526C0.131865 2.81636 0.20839 2.63612 0.341986 2.50252C0.475582 2.36893 0.655817 2.2924 0.844721 2.28907C1.03363 2.28574 1.21645 2.35586 1.35467 2.48466L3.01231 4.14132L6.62898 0.31174C6.76179 0.171251 6.94496 0.0892577 7.13822 0.0837883C7.33147 0.0783189 7.51899 0.149821 7.65953 0.282573Z" fill="inherit"/>
							</svg>
							<Typography key={item} sx={clsx(podiumStyles['goodsText'])}>{item}</Typography>
						</Box>
					))}
        </Stack>
      </Box>
    </>
  )

  const goldPlaceContent = (
    <>
      <Box sx={clsx(podiumStyles['goldWrapper'])}>
        <Box sx={clsx(podiumStyles['goldEffect'])}>
          <img src={images.goldEffect} alt="Gold effect" width={400} height={315} />
        </Box>
        <Box sx={clsx(podiumStyles['goldMedalWrapper'])}>
          <img src={images.goldAvatarWrapper} alt="Gold avatar" width={152} height={170} />
          <Box sx={clsx(podiumStyles['goldAvatarImage'])}>
            {leaders[0]?.community?.avatarId ? (
              <img src={`${images.baseUrl}/${leaders[0]?.community?.avatarId}`} alt={leaders[0]?.community?.fullName} style={podiumStyles['avatarImg'] as CSSProperties} />
            ) : (
              <AvatarMock style={{ borderRadius: '50%', width: '100%', height: '100%' }}/>
            )}
          </Box>
          <Box sx={clsx(podiumStyles['positionWrapper'])}>
            <img src={images.goldPosition} alt="Gold position" width={58} height={42} />
          </Box>
        </Box>
        <Box sx={clsx(podiumStyles['goldNameWrapper'])}>
          <Typography sx={clsx(podiumStyles['communityName'])}>{leaders[0]?.community?.fullName}</Typography>
          <Typography sx={clsx(podiumStyles['score'])}>{scoreName ? `${leaders[0]?.value} ${scoreName}` : ''}</Typography>
        </Box>
      </Box>
      <Box sx={clsx(podiumStyles['goldPodiumWrapper'])}>
        <img src={images.goldPodium} alt="Gold podium" width={278} height={194} />
        <Stack sx={{ position: 'absolute', top: '67px', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
          <Box sx={clsx(podiumStyles['prizeWrapper'])}>
            <img src={images.goldTrophy} alt="Gold trophy" width={prizes ? 30 : 60} height={prizes ? 31 : 62} />
						<Showable show={prizes?.gold?.prize}>
							<Typography sx={clsx(podiumStyles['prizeAmount'])}>
								$ {prizes?.gold?.prize}
							</Typography>
						</Showable>
          </Box>
					{prizes?.gold?.goods?.map((item, index) => (
						<Box sx={clsx(podiumStyles["goldGoodsWrapper"])} key={`good-${index + 1}`}>
							<svg width="8" height="6" viewBox="0 0 8 6" fill="rgba(255, 255, 255, 0.7)" xmlns="http://www.w3.org/2000/svg">
								<path d="M7.65953 0.282573C7.80002 0.415388 7.88202 0.598559 7.88749 0.791812C7.89295 0.985066 7.82145 1.17258 7.6887 1.31313L3.55676 5.68813C3.48979 5.75891 3.40932 5.81555 3.32009 5.8547C3.23086 5.89385 3.1347 5.91473 3.03727 5.91609C2.93984 5.91744 2.84312 5.89926 2.75284 5.86261C2.66256 5.82597 2.58053 5.77159 2.51162 5.70271L0.324118 3.51521C0.195318 3.37699 0.125199 3.19416 0.128532 3.00526C0.131865 2.81636 0.20839 2.63612 0.341986 2.50252C0.475582 2.36893 0.655817 2.2924 0.844721 2.28907C1.03363 2.28574 1.21645 2.35586 1.35467 2.48466L3.01231 4.14132L6.62898 0.31174C6.76179 0.171251 6.94496 0.0892577 7.13822 0.0837883C7.33147 0.0783189 7.51899 0.149821 7.65953 0.282573Z" fill="inherit"/>
							</svg>
							<Typography key={item} sx={clsx(podiumStyles['goldGoodsText'])}>{item}</Typography>
						</Box>
					))}
        </Stack>
      </Box>
    </>
  )

  const bronzePlaceContent = (
    <>
      <Box sx={clsx(podiumStyles['bronzeWrapper'])}>
        <Box sx={clsx(podiumStyles['bronzeEffect'])}>
          <img src={images.bronzeEffect} alt="Bronze effect" width={400} height={400} />
        </Box>
        <Box sx={clsx(podiumStyles['medalWrapper'])}>
          <img src={images.bronzeAvatarWrapper} alt="Bronze effect" width={114} height={116} />
          <Box sx={clsx(podiumStyles['avatarImage'])}>
            {leaders[2]?.community?.avatarId ? (
              <img src={`${images.baseUrl}/${leaders[2]?.community?.avatarId}`} alt={leaders[2]?.community?.fullName} style={podiumStyles['avatarImg'] as CSSProperties} />
            ) : (
              <AvatarMock style={{ borderRadius: '50%', width: '100%', height: '100%' }}/>
            )}
          </Box>
          <Box sx={clsx(podiumStyles['positionWrapper'])}>
            <img src={images.bronzePosition} alt="Bronze position" width={70} height={31} />
          </Box>
        </Box>
        <Box sx={clsx(podiumStyles['bronzeNameWrapper'])}>
          <Typography sx={clsx(podiumStyles['communityName'])}>{leaders[2]?.community?.fullName}</Typography>
          <Typography sx={clsx(podiumStyles['score'])}>{scoreName ? `${leaders[2]?.value} ${scoreName}` : ''}</Typography>
        </Box>
      </Box>
      <Box sx={clsx(podiumStyles['bronzePodiumWrapper'])}>
        <img src={images.bronzePodium} alt="Bronze podium" width={257} height={180} />
        <Stack sx={{ position: 'absolute', top: '67px', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '11px' }}>
            <img src={images.bronzeTrophy} alt="Bronze trophy" width={prizes ? 30 : 60} height={prizes ? 31 : 62} />
						<Showable show={prizes?.bronze?.prize}>
							<Typography sx={clsx(podiumStyles['prizeAmount'])}>
								$ {prizes?.bronze?.prize}
							</Typography>
						</Showable>
          </Box>
					{prizes?.bronze?.goods?.map((item, index) => (
						<Box sx={clsx(podiumStyles['goodsWrapper'])} key={`good-${index + 1}`}>
							<svg width="8" height="6" viewBox="0 0 8 6" fill="rgba(255, 255, 255, 0.7)" xmlns="http://www.w3.org/2000/svg">
								<path d="M7.65953 0.282573C7.80002 0.415388 7.88202 0.598559 7.88749 0.791812C7.89295 0.985066 7.82145 1.17258 7.6887 1.31313L3.55676 5.68813C3.48979 5.75891 3.40932 5.81555 3.32009 5.8547C3.23086 5.89385 3.1347 5.91473 3.03727 5.91609C2.93984 5.91744 2.84312 5.89926 2.75284 5.86261C2.66256 5.82597 2.58053 5.77159 2.51162 5.70271L0.324118 3.51521C0.195318 3.37699 0.125199 3.19416 0.128532 3.00526C0.131865 2.81636 0.20839 2.63612 0.341986 2.50252C0.475582 2.36893 0.655817 2.2924 0.844721 2.28907C1.03363 2.28574 1.21645 2.35586 1.35467 2.48466L3.01231 4.14132L6.62898 0.31174C6.76179 0.171251 6.94496 0.0892577 7.13822 0.0837883C7.33147 0.0783189 7.51899 0.149821 7.65953 0.282573Z" fill="inherit"/>
							</svg>
							<Typography key={item} sx={clsx(podiumStyles['goodsText'])}>{item}</Typography>
						</Box>
					))}
        </Stack>
      </Box>
    </>
  )

  return (
    <Box sx={clsx(podiumStyles['wrapper'])}>
      {/* silver */}
			<Link href={getCommunityPageUrl(leaders[1]?.community?.userName)} target="_blank">
				{silverPlaceContent}
			</Link>
      {/* gold */}
			<Link href={getCommunityPageUrl(leaders[0]?.community?.userName)} target="_blank">
				{goldPlaceContent}
			</Link>
      {/* bronze */}
			<Link href={getCommunityPageUrl(leaders[2]?.community?.userName)} target="_blank">
				{bronzePlaceContent}
			</Link>
      {/* stars */}
      <Box sx={clsx(podiumStyles['starsEffect'])}>
        <img src={images.starsEffect} alt="Stars effect" width={436} height={230} />
      </Box>
      {/* gradient */}
      <Box sx={clsx(podiumStyles['gradient'])}/>
    </Box>
  )
}
